import { useMemo, useState } from 'react';
import type { CalendarDate } from '@internationalized/date';
import { Button, Card, Chip, Description, Heading, Separator } from '@heroui/react';
import { CalendarDays, X } from 'lucide-react';
import type { AvailabilityWindow } from '../../../api/my-parking-spots';
import {
  calendarDateTimeToDate,
  formatCalendarDateLongBR,
  getCurrentCalendarDateTime,
} from '../../../utils/date-utils';
import {
  availabilityWindowToCalendarDate,
  getWindowsOnDate,
  groupWindowsByStartDate,
} from '../utils/availability-windows';
import { ActiveWindowsMonthCalendar } from './active-windows-month-calendar';
import { ActiveWindowRow } from './active-window-row';

interface ActiveWindowsAgendaViewProps {
  windows: AvailabilityWindow[];
  onEditWindow: (window: AvailabilityWindow) => void;
  onCancelWindow: (window: AvailabilityWindow) => void;
}

export function ActiveWindowsAgendaView({
  windows,
  onEditWindow,
  onCancelWindow,
}: ActiveWindowsAgendaViewProps) {
  const [selectedDate, setSelectedDate] = useState<CalendarDate | null>(null);
  const [highlightedWindowId, setHighlightedWindowId] = useState<string | null>(null);
  const [calendarKey, setCalendarKey] = useState(0);

  const defaultFocusedValue = useMemo(() => {
    const firstWindowDate = availabilityWindowToCalendarDate(windows[0]?.startAt);
    return firstWindowDate ?? calendarDateTimeToDate(getCurrentCalendarDateTime());
  }, [windows]);

  const groupedWindows = useMemo(() => groupWindowsByStartDate(windows), [windows]);
  const filteredWindows = selectedDate ? getWindowsOnDate(selectedDate, windows) : windows;

  const handleSelectedDateChange = (date: CalendarDate) => {
    setSelectedDate(date);
    setHighlightedWindowId(null);
  };

  const handleClearDateFilter = () => {
    setSelectedDate(null);
    setHighlightedWindowId(null);
    setCalendarKey((key) => key + 1);
  };

  return (
    <div className="flex flex-col gap-4">
      <Card variant="secondary">
        <Card.Header className="gap-1 border-b border-separator pb-3">
          <Card.Title className="flex items-center gap-2 text-base font-semibold">
            <CalendarDays aria-hidden className="size-4 text-muted" />
            Calendário
          </Card.Title>
          <Card.Description>
            Dias com ponto têm janela ativa. Selecione um dia para filtrar a agenda abaixo.
          </Card.Description>
        </Card.Header>
        <Card.Content className="pt-4">
          <ActiveWindowsMonthCalendar
            key={calendarKey}
            windows={windows}
            selectedDate={selectedDate}
            highlightedWindowId={highlightedWindowId}
            defaultFocusedValue={defaultFocusedValue}
            onSelectedDateChange={handleSelectedDateChange}
          />
        </Card.Content>
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <Heading level={4} className="text-base font-semibold">
          {selectedDate ? formatCalendarDateLongBR(selectedDate) : 'Agenda'}
        </Heading>
        {selectedDate ? (
          <Button size="sm" variant="secondary" onPress={handleClearDateFilter}>
            <X size={14} />
            Ver todas
          </Button>
        ) : (
          <Chip size="sm" variant="soft">
            {windows.length} {windows.length === 1 ? 'janela' : 'janelas'}
          </Chip>
        )}
      </div>

      {filteredWindows.length === 0 ? (
        <Description className="py-4 text-center">
          Nenhuma janela ativa neste dia.
        </Description>
      ) : selectedDate ? (
        <div className="flex flex-col gap-3">
          {filteredWindows.map((window) => (
            <ActiveWindowRow
              key={window.id}
              window={window}
              isHighlighted={highlightedWindowId === window.id}
              onHighlight={() =>
                setHighlightedWindowId((current) => (current === window.id ? null : window.id))
              }
              onEdit={() => onEditWindow(window)}
              onCancel={() => onCancelWindow(window)}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          {groupedWindows.map((group) => (
            <section key={`${group.date.year}-${group.date.month}-${group.date.day}`}>
              <div className="mb-3 flex items-center gap-3">
                <Heading level={5} className="text-sm font-semibold text-muted">
                  {formatCalendarDateLongBR(group.date)}
                </Heading>
                <Separator className="flex-1" />
              </div>
              <div className="flex flex-col gap-3">
                {group.windows.map((window) => (
                  <ActiveWindowRow
                    key={window.id}
                    window={window}
                    isHighlighted={highlightedWindowId === window.id}
                    onHighlight={() =>
                      setHighlightedWindowId((current) =>
                        current === window.id ? null : window.id,
                      )
                    }
                    onEdit={() => onEditWindow(window)}
                    onCancel={() => onCancelWindow(window)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
